/**
 * Утилиты для работы с версиями в формате semver
 */

import { IVersionUtils } from '../types/migration-interfaces';

/**
 * Реализация утилит для работы с версиями
 */
export class VersionUtils implements IVersionUtils {
  private static readonly VERSION_REGEX = /^(\d+)\.(\d+)\.(\d+)$/;

  /**
   * Сравнение версий
   * @returns -1 если version1 < version2, 0 если равны, 1 если version1 > version2
   */
  compare(version1: string, version2: string): number {
    const v1 = this.parse(version1);
    const v2 = this.parse(version2);

    if (v1.major !== v2.major) {
      return v1.major - v2.major;
    }
    
    if (v1.minor !== v2.minor) {
      return v1.minor - v2.minor;
    }
    
    return v1.patch - v2.patch;
  }

  /**
   * Проверка валидности версии
   */
  isValid(version: string): boolean {
    return VersionUtils.VERSION_REGEX.test(version);
  }

  /**
   * Получение следующей версии
   */
  getNext(version: string, type: 'major' | 'minor' | 'patch' = 'patch'): string {
    const parsed = this.parse(version);
    
    switch (type) {
      case 'major':
        return this.format(parsed.major + 1, 0, 0);
      case 'minor':
        return this.format(parsed.major, parsed.minor + 1, 0);
      case 'patch':
        return this.format(parsed.major, parsed.minor, parsed.patch + 1);
      default:
        throw new Error(`Unknown version type: ${type}`);
    }
  }

  /**
   * Парсинг версии
   */
  parse(version: string): { major: number; minor: number; patch: number } {
    const match = version.match(VersionUtils.VERSION_REGEX);
    
    if (!match) {
      throw new Error(`Invalid version format: ${version}`);
    }

    return {
      major: parseInt(match[1], 10),
      minor: parseInt(match[2], 10),
      patch: parseInt(match[3], 10)
    };
  }

  /**
   * Форматирование версии
   */
  format(major: number, minor: number, patch: number): string {
    return `${major}.${minor}.${patch}`;
  }

  /**
   * Получение диапазона версий
   */
  getRange(fromVersion: string, toVersion: string): string[] {
    const from = this.parse(fromVersion);
    const to = this.parse(toVersion);
    
    const versions: string[] = [];
    
    for (let major = from.major; major <= to.major; major++) {
      const minorStart = major === from.major ? from.minor : 0;
      const minorEnd = major === to.major ? to.minor : 999;
      
      for (let minor = minorStart; minor <= minorEnd; minor++) {
        const patchStart = (major === from.major && minor === from.minor) ? from.patch : 0;
        const patchEnd = (major === to.major && minor === to.minor) ? to.patch : 999;
        
        for (let patch = patchStart; patch <= patchEnd; patch++) {
          const version = this.format(major, minor, patch);
          versions.push(version);
        }
      }
    }
    
    return versions;
  }

  /**
   * Проверка, находится ли версия в диапазоне
   */
  isInRange(version: string, fromVersion: string, toVersion: string): boolean {
    const compareFrom = this.compare(version, fromVersion);
    const compareTo = this.compare(version, toVersion);
    
    return compareFrom >= 0 && compareTo <= 0;
  }

  /**
   * Получение максимальной версии из списка
   */
  getMax(versions: string[]): string {
    if (versions.length === 0) {
      throw new Error('No versions provided');
    }
    
    return versions.reduce((max, current) => 
      this.compare(current, max) > 0 ? current : max
    );
  }

  /**
   * Получение минимальной версии из списка
   */
  getMin(versions: string[]): string {
    if (versions.length === 0) {
      throw new Error('No versions provided');
    }
    
    return versions.reduce((min, current) => 
      this.compare(current, min) < 0 ? current : min
    );
  }

  /**
   * Сортировка версий
   */
  sort(versions: string[], ascending: boolean = true): string[] {
    return versions.sort((a, b) => {
      const compare = this.compare(a, b);
      return ascending ? compare : -compare;
    });
  }
} 